import { useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import { useCreateEventPoll, useUpdateEventPoll } from "@/api/bale";
import { isValidationErrorDetail } from "@/compat";
import { Button } from "@/components/Button";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { Checkbox } from "@/components/Checkbox";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { ErrorDetail } from "@/components/ErrorDetail";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { IconButton } from "@/components/IconButton";
import { Input, InputContainer } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { uuidVersion } from "@/fp/uuidVersion";
import { zip } from "@/fp/zip";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";
import { useEventWidgets } from "@/pages/EventPage/hooks/useEventWidgets";

import type { PollWidget } from "./PollCard";

type Props = {
  widget: PollWidget;
  onSave: () => void;
  onCancel?: () => void;
};

export function EditingPollCard({ widget, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const { replaceWidget, removeWidget } = useEventWidgets();
  const { data: eventId } = useCurrentEventSuspense({ query: { select: (data) => data.data.event.id } });

  const [options, setOptions] = useState<(number | string)[]>(() => {
    const opts = widget.options?.map((opt) => opt.id) ?? [];
    if (opts.length === 0) return [`x${crypto.randomUUID()}`, `x${crypto.randomUUID()}`];
    return opts;
  });

  const updatePoll = useUpdateEventPoll({
    mutation: {
      onSuccess: ({ data: { event_poll } }) => {
        replaceWidget(widget.id, { ...widget, ...event_poll });
        onSave();
      },
    },
  });

  const createPoll = useCreateEventPoll({
    mutation: {
      onSuccess: ({ data: { event_poll } }) => {
        replaceWidget(widget.id, event_poll);
        onSave();
      },
    },
  });

  function handleSubmit(submitEvent: SubmitEvent<HTMLFormElement>) {
    const formData = new FormData(submitEvent.currentTarget);
    const subject = formData.get("subject")! as string;
    const allow_custom_options = formData.has("allow_custom_options");
    const is_anonymous = formData.has("is_anonymous");
    const is_response_editable = formData.has("is_response_editable");
    const is_result_visible_before_voting = !formData.has("is_result_hidden_until_voting");
    const max_selections = Number.parseInt(formData.get("max_selections")! as string, 10);
    const options = zip(
      formData.getAll("options[].id").map((id) => Number.parseInt(id as string, 10)),
      formData.getAll("options[].title"),
    ).map(([id, title]) => ({
      id: Number.isNaN(id) ? null : id,
      title: title as string,
    }));
    if (uuidVersion(widget.id) === 7) {
      updatePoll.mutate({
        widgetId: widget.id,
        data: {
          subject,
          allow_custom_options,
          is_anonymous,
          is_response_editable,
          is_result_visible_before_voting,
          max_selections,
          options,
        },
      });
    } else {
      createPoll.mutate({
        eventId,
        data: {
          subject,
          allow_custom_options,
          is_anonymous,
          is_response_editable,
          is_result_visible_before_voting,
          max_selections,
          options,
        },
      });
    }
  }

  return (
    <Card>
      <Form error={updatePoll.error ?? createPoll.error} onSubmit={handleSubmit}>
        <DragHandle>
          <CardActionsHeader title={t("event-poll")}>
            <IconButton
              type="button"
              name="trash"
              className="text-danger"
              disabled={updatePoll.isPending || createPoll.isPending}
              onClick={() => removeWidget(widget.id)}
            />
            {!!onCancel && (
              <IconButton
                type="button"
                name="x"
                aria-label={t("cancel")}
                disabled={updatePoll.isPending || createPoll.isPending}
                onClick={onCancel}
              />
            )}
            <IconButton
              type="submit"
              name="save"
              aria-label={t("save")}
              disabled={updatePoll.isPending || createPoll.isPending}
            />
          </CardActionsHeader>
        </DragHandle>
        <CardTextContent className="flex flex-col gap-4">
          {!!updatePoll.error?.info && !isValidationErrorDetail(updatePoll.error.info) && (
            <ErrorDetail error={updatePoll.error.info} />
          )}
          {!!createPoll.error?.info && !isValidationErrorDetail(createPoll.error.info) && (
            <ErrorDetail error={createPoll.error.info} />
          )}
          <FormField title={t("event-poll-subject")}>
            <Textarea
              name="subject"
              defaultValue={widget.subject?.raw ?? ""}
              placeholder={t("event-poll-subject-placeholder")}
              disabled={updatePoll.isPending || createPoll.isPending}
            />
          </FormField>
          <FormField title={t("event-poll-max-selections")}>
            <Input
              name="max_selections"
              type="number"
              min="1"
              defaultValue={widget.max_selections ?? ""}
              placeholder={t("event-poll-max-selections-placeholder")}
              disabled={updatePoll.isPending || createPoll.isPending}
            />
          </FormField>
          <div className="flex flex-row flex-wrap gap-4">
            <label className="flex items-center gap-4">
              <Checkbox
                defaultChecked={widget.is_anonymous}
                name="is_anonymous"
                disabled={updatePoll.isPending || createPoll.isPending}
              />
              <p className="font-interest">{t("poll-setting-is-anonymous")}</p>
            </label>
            <label className="flex items-center gap-4">
              <Checkbox
                defaultChecked={widget.is_response_editable}
                name="is_response_editable"
                disabled={updatePoll.isPending || createPoll.isPending}
              />
              <p className="font-interest">{t("poll-setting-is-response-editable")}</p>
            </label>
            <label className="flex items-center gap-4">
              <Checkbox
                defaultChecked={!widget.is_result_visible_before_voting}
                name="is_result_hidden_until_voting"
                disabled={updatePoll.isPending || createPoll.isPending}
              />
              <p className="font-interest">{t("poll-setting-is-result-visible-before-voting")}</p>
            </label>
          </div>

          <div className="flex flex-col gap-2">
            {options.map((option, i) => (
              <div key={option} className="flex flex-row gap-2 rounded-sm border border-divider bg-black/2 p-2">
                <input type="hidden" name="options[].id" value={option} />
                <InputContainer className="grow">
                  <Input
                    className="grow"
                    type="text"
                    name="options[].title"
                    validationName={`options.${i}.title`}
                    placeholder={t("poll-option-n", { n: i + 1 })}
                    defaultValue={
                      typeof option === "number" ? widget.options?.find((opt) => opt.id === option)?.title : ""
                    }
                  />
                </InputContainer>
                <IconButton
                  type="button"
                  name="trash"
                  onClick={() => setOptions((opts) => opts.filter((opt) => opt !== option))}
                />
              </div>
            ))}
            <Button type="button" onClick={() => setOptions((opts) => [...opts, `x${crypto.randomUUID()}`])}>
              {t("add-option")}
            </Button>
          </div>
        </CardTextContent>
      </Form>
    </Card>
  );
}
